import React, { PropsWithChildren, ReactElement, useContext } from 'react';

import PropTypes from 'prop-types';
import { Row } from 'react-table';
import { CSSObject } from 'styled-components';

import { stylePropType, StylableComponent } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import TablePropTypes from './propTypes';
import TableBodyCell from './TableBodyCell';
import TableSizeThemeContext from './TableSizeThemeContext';

export type TableBodyRowProps<D extends Record<string, unknown>> =
  StylableComponent<HTMLDivElement> & {
    row: Row<D>;
  };

const TableBodyRow = <D extends Record<string, unknown>>({
  row,
  children,
  style,
  ...props
}: PropsWithChildren<TableBodyRowProps<D>>): ReactElement => {
  const { tableSizeTheme } = useContext(TableSizeThemeContext);
  const isSelectable =
    row.hasOwnProperty('toggleRowSelected') && !row.state?.isDisabled;
  return (
    <div
      data-testid={`row-${row.id}`}
      data-index={row.index}
      role="row"
      onClick={() => isSelectable && row.toggleRowSelected?.(!row.isSelected)}
      {...props}
      css={[
        {
          height: tableSizeTheme.rowHeight,
          ...(row.state?.isDisabled
            ? {
                color: theme.colors.charcoal500,
              }
            : {}),
          ...(isSelectable
            ? {
                cursor: 'pointer',
                '&:hover': {
                  '& > [role="cell"]': {
                    backgroundColor: theme.colors.charcoal050,
                  },
                },
                '&:active': {
                  '& > [role="cell"]': {
                    backgroundColor: theme.colors.charcoal100,
                  },
                },
                ...(row.isSelected
                  ? {
                      '& > [role="cell"]': {
                        // FIXME: Hardcoding opacity 35% on adskBlue100 as hex to prevent
                        // underlapping sticky columns
                        backgroundColor: '#f5fbfd',
                      },
                    }
                  : {}),
              }
            : {}),
        },
        style,
        { minWidth: 'fit-content' }, // Overwrite `style`'s min-width to fix sticky column
      ]}
    >
      {children
        ? React.Children.map(children, (child, index) => {
            const cell = row.cells[index];
            return React.cloneElement(child as ReactElement, { cell });
          })
        : row.cells.map((cell) => {
            const { style, ...cellProps } = cell.getCellProps();
            return (
              <TableBodyCell<D>
                {...cellProps}
                style={style as CSSObject}
                key={cellProps.key}
                cell={cell}
              />
            );
          })}
    </div>
  );
};

TableBodyRow.propTypes = {
  children: PropTypes.node,
  row: TablePropTypes.row.isRequired,
  style: stylePropType,
};

export default TableBodyRow;
